import { ArrowLeftRight, ClipboardCheck, History, Ruler, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  formatQuantity,
  isAreaUnitCode,
  type UnitOfMeasure,
} from '@obraplan/core/domain/schedule/unit-of-measure';
import { Hours } from '@obraplan/core/domain/shared/hours';
import { isOvertime } from '@obraplan/core/domain/planning/capacity-calculator';
import { MIN_JUSTIFICATION_LENGTH } from '@obraplan/core/domain/planning/over-capacity-policy';
import { quarterHoursFromDecimal } from '@obraplan/core/domain/planning/weekly-allocation';
import type { Attachment } from '@obraplan/core/domain/attachments/attachment';
import type { InspectionLine } from '@obraplan/core/application/use-cases/planning/inspection-use-cases';
import type { AttachmentUsage } from '@obraplan/core/application/use-cases/planning/attachment-use-cases';
import type {
  DayQualityFrontView,
  DayQualityView,
} from '@obraplan/core/application/use-cases/quality/quality-use-cases';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input, Textarea } from '@/components/ui/input';
import { AreaMeasurementButton } from './AreaMeasurementDialog';
import { AttachmentGallery } from './AttachmentGallery';
import { PecasDoDia, type CarregarPecas } from './PecasDoDia';
import type { StepMeasurement } from './NonConformityFields';
import { QualityVerificationStep, type NonConformityPhotos } from './QualityVerificationStep';
import {
  frontDraftToInput,
  pendingPhotos,
  type OwnerOf,
  type PendingNonConformityPhotos,
  frontHasVerification,
  initialFrontDraft,
  isFrontDraftComplete,
  summarizeFrontDraft,
  type FrontDraft,
  type VerificationInput,
} from './quality-verification-draft';

/**
 * O dia de uma pessoa, numa caixa só.
 *
 * Confirmar responde "a pessoa cumpriu o dia?"; medir responde "e rendeu quanto?". Eram
 * dois botões no cartão e viraram um (Fase 20): o cartão abre esta caixa, e é aqui que as
 * horas se ajustam, a quantidade se lança, a checklist se responde, a foto se anexa — e é
 * daqui que o dia se confirma, no mesmo gesto. Dois caminhos para a mesma caixa eram dois
 * jeitos de chegar ao mesmo lugar, e quem estava no canteiro não sabia qual escolher.
 *
 * **Confirmar encerra o dia.** Com hora ajustada ele encerra como *ajustado* — o próprio
 * ajuste já o encerra (RN-104) —, e sem ajuste, como *confirmado*. Depois disso as horas
 * só mudam reabrindo, e é o cartão que reabre.
 *
 * Medição e fotos moram juntas porque são o mesmo ato no canteiro: o encarregado olha a
 * frente, anota o que saiu e fotografa o que anotou. Separar em duas telas obrigaria a
 * percorrer o mesmo caminho duas vezes.
 *
 * ## E, desde a Fase 19, "ficou bom?" — num segundo passo
 *
 * A quantidade nova numa frente com serviço cadastrado só grava com a verificação de
 * qualidade (RN-218). Ela é o segundo passo do mesmo diálogo, uma frente por vez: o
 * primeiro continua sendo quantidades e fotos, e cada frente mostra ali o estado da sua
 * verificação e o botão para entrar nela. O botão de salvar antecipa a regra e fica
 * desabilitado enquanto uma frente com quantidade nova estiver sem verificação — quem a
 * garante é o caso de uso.
 *
 * O rascunho é chaveado pela **conferência aberta**, e não pela linha: a linha é objeto
 * novo a cada revalidação (anexar uma foto revalida a tela inteira), e reiniciar por ela
 * apagaria sete respostas porque alguém tirou uma foto.
 *
 * Fechar sem preencher é resposta legítima: nem toda frente é medida, e nem todo dia tem
 * foto. O que foi confirmado continua confirmado. E **só o que mudou é enviado**: gravar
 * a mesma quantidade não é medição nova, e não pode custar a verificação das outras.
 */
export interface DayMeasurementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  line: InspectionLine | undefined;
  /**
   * As horas que os lançamentos da fila do aparelho ainda vão criar para a pessoa no dia, fora
   * das frentes do cartão (RN-642). O servidor as grava antes do ajuste, e a hora extra as conta.
   */
  queuedQuarterHours?: number;
  /** A conferência aberta — é por ela que o rascunho é chaveado. */
  inspectionId: string | undefined;
  dateLabel: string;
  /** A qualidade do dia. `undefined` enquanto carrega — e aí não se grava quantidade nova. */
  quality: DayQualityView | undefined;
  /** As fotos das NCs já abertas (fatia 6). Ausente = sem galeria no passo 2. */
  nonConformityPhotos?: NonConformityPhotos | undefined;
  /** Anexos da conferência — a galeria é a mesma do botão de fotos. */
  attachments: readonly Attachment[];
  usage: AttachmentUsage | undefined;
  canAttach: boolean;
  canRemove: boolean;
  isSaving: boolean;
  isAttaching: boolean;
  errorMessage?: string;
  attachErrorMessage?: string;
  resolveUrl: (attachmentId: string) => Promise<string | undefined>;
  onAttach: (files: readonly File[]) => void;
  onRemoveAttachment: (attachmentId: string) => void;
  /**
   * As horas de cada frente podem ser ajustadas aqui (Fase 20) — é o mesmo ajuste da
   * conferência (RN-104), só que sem um diálogo por frente. Só enquanto a conferência
   * está aberta; encerrada, o diálogo diz para reabrir.
   */
  canAdjust?: boolean;
  /**
   * Tira a frente do dia (RN-631) — a que não aconteceu, lançada pelo RDO ou planejada. Aparece
   * junto das horas, com a mesma regra delas: quem ajusta, com a conferência aberta. Pede uma
   * confirmação na própria linha, e grava na hora, fora do "Salvar".
   */
  onRemoveFront?: ((front: { allocationId: string; expectedVersion: number }) => void) | undefined;
  /**
   * Troca a atividade lançada errada (RN-645): quem chama fecha o diálogo e leva a frente para o
   * "Lançar trabalho", onde se escolhe a certa. Aparece para quem ajusta, com a conferência aberta
   * ou encerrada — a troca reabre a encerrada.
   */
  onReplaceFront?: ((allocationId: string) => void) | undefined;
  /** Abre o histórico de alterações da pessoa no dia; presente só quando há o que ver. */
  onHistory?: (() => void) | undefined;
  /**
   * A descrição da próxima foto (RN-214) — opcional aqui, como no caso de uso.
   *
   * Mora em quem chama, e não aqui, pelo mesmo motivo da galeria do RDO: é quem envia o
   * arquivo que a manda junto, e guardá-la em dois lugares as faria divergir.
   */
  caption?: { value: string; onChange: (value: string) => void } | undefined;
  /**
   * Quem pode encerrar o dia (`LABOR_CONFIRM`) confirma daqui — é o botão que o cartão
   * perdeu. Com a conferência já encerrada não há o que confirmar, e o botão volta a ser
   * o de salvar a medição.
   */
  canConfirm?: boolean;
  /**
   * O apontamento por peça (Fase 27, etapa 6): a frente × serviço que mede peça mostra as
   * peças do dia sob o campo de quantidade. Ausente = a caixa é a de sempre, sem peças.
   */
  pieces?: { readonly date: string; readonly load: CarregarPecas } | undefined;
  /**
   * Na obra por equipe, a quantidade de quem está numa equipe é da equipe (Fase 30, RN-406): o
   * nome dela vem aqui, como sai no RDO ("Equipe Alvenaria"), e a caixa mede só as horas — o
   * número se lança no painel da equipe.
   * Ausente = a pessoa mede como sempre (por pessoa, sem equipe, terceirizado — RN-407).
   */
  quantityOfTeam?: string | undefined;
  onSave: (
    measurements: readonly { allocationId: string; executedQuantity: number | null }[],
    verifications: readonly VerificationInput[],
    extras?: {
      readonly adjustments: readonly {
        allocationId: string;
        expectedVersion: number;
        quarterHours: number;
      }[];
      readonly justification?: string;
      /** Encerrar o dia ao gravar. Com ajuste de horas ele já encerra sozinho (RN-104). */
      readonly confirm?: boolean;
      /**
       * As peças marcadas, uma entrada por linha **em que a pessoa mexeu** (RN-381): a lista
       * substitui as marcas do dia daquela frente × serviço. Quem chama grava antes da
       * quantidade, e uma recusa (peça já executada em outro dia) para a sequência.
       */
      readonly pieces?: readonly {
        readonly allocationId: string;
        readonly activityId: string;
        readonly serviceId: string;
        readonly pieceNames: readonly string[];
        /** As do dia quando a lista abriu: a fila manda só a diferença (RN-615). */
        readonly previousPieceNames?: readonly string[];
      }[];
      /**
       * As fotos escolhidas nos itens reprovados, antes de a NC existir (Fase 37, RN-479):
       * quem chama as sobe depois de gravar, pela referência que a gravação devolve.
       */
      readonly photos?: readonly PendingNonConformityPhotos[];
    },
  ) => void;
}

interface Draft {
  readonly allocationId: string;
  readonly activityId: string;
  readonly label: string;
  readonly unit: UnitOfMeasure;
  readonly planned: number | undefined;
  /** O que estava gravado ao abrir — é contra ele que "mudou" é decidido. */
  readonly executed: number | undefined;
  readonly quarterHours: number;
}

type Passo = { kind: 'measure' } | { kind: 'verify'; activityId: string };

export function DayMeasurementDialog({
  open,
  onOpenChange,
  line,
  queuedQuarterHours = 0,
  inspectionId,
  dateLabel,
  quality,
  nonConformityPhotos,
  attachments,
  usage,
  canAttach,
  canRemove,
  isSaving,
  isAttaching,
  errorMessage,
  attachErrorMessage,
  resolveUrl,
  onAttach,
  onRemoveAttachment,
  canAdjust = false,
  onRemoveFront,
  onReplaceFront,
  onHistory,
  caption,
  canConfirm = false,
  pieces,
  quantityOfTeam,
  onSave,
}: DayMeasurementDialogProps) {
  // Só as frentes medidas entram: pedir quantidade de algo sem unidade é pedir um número
  // que ninguém sabe ler depois. A quantidade da equipe também não entra: é lançada uma vez,
  // no painel dela, e não pessoa a pessoa.
  const measurable: Draft[] = (line?.allocations ?? []).flatMap(
    ({ activity, allocation, service, unit }) =>
      unit === undefined || quantityOfTeam !== undefined
        ? []
        : [
            {
              allocationId: allocation.id,
              activityId: activity.activityId,
              // A frente diz onde, o serviço diz o quê — e quem mede precisa dos dois: a
              // mesma alvenaria do 9º tem assentamento e verga no mesmo dia.
              label: service === undefined ? activity.label : `${activity.label} · ${service.name}`,
              unit,
              planned: allocation.plannedQuantity,
              executed: allocation.executedQuantity,
              quarterHours: allocation.quarterHours,
            },
          ],
  );

  const [values, setValues] = useState<Readonly<Record<string, string>>>({});
  const [hoursValues, setHoursValues] = useState<Readonly<Record<string, string>>>({});
  const [justificativa, setJustificativa] = useState('');
  const [drafts, setDrafts] = useState<Readonly<Record<string, FrontDraft>>>({});
  const [touched, setTouched] = useState<ReadonlySet<string>>(new Set());
  const [passo, setPasso] = useState<Passo>({ kind: 'measure' });
  // As peças marcadas por linha — só as que a pessoa mexeu; o resto fica como o servidor tem.
  const [pecas, setPecas] = useState<Readonly<Record<string, readonly string[]>>>({});
  // As que já estavam no dia quando a lista de cada linha abriu: a fila manda só a diferença
  // entre estas e as escolhidas, sobre a lista do servidor na hora de subir (Fase 43, RN-615).
  const [pecasVistas, setPecasVistas] = useState<Readonly<Record<string, readonly string[]>>>({});
  // O que as peças puseram no campo de cada linha: desmarcar todas zera só o que elas
  // puseram — o número digitado à mão continua sendo de quem digitou.
  const [doPreenchimento, setDoPreenchimento] = useState<Readonly<Record<string, string>>>({});
  // A frente cujo "Tirar" foi tocado, esperando a confirmação na linha dela.
  const [aTirar, setATirar] = useState<string | undefined>(undefined);

  // A linha é lida por referência no reinício: o efeito depende só de `open` e da
  // conferência, que são estáveis — uma foto anexada revalida a linha, e não pode
  // apagar o que estava sendo digitado.
  const lineRef = useRef(line);
  lineRef.current = line;

  useEffect(() => {
    if (!open) return;
    /*
     * Abre com o executado já lançado, ou com a **meta** quando ainda não houve medição:
     * no dia comum saiu o combinado, e obrigar a redigitar o número que já está na tela é
     * o caminho mais curto para ninguém medir. Quem rendeu diferente sobrescreve — que é
     * o caso raro, e é o que merece o trabalho.
     */
    const initial: Record<string, string> = {};
    const horas: Record<string, string> = {};
    for (const { activity: _a, allocation, unit } of lineRef.current?.allocations ?? []) {
      horas[allocation.id] = String(allocation.quarterHours / 4);
      if (unit === undefined) continue;
      const current = allocation.executedQuantity ?? allocation.plannedQuantity;
      initial[allocation.id] = current === undefined ? '' : String(current);
    }
    setValues(initial);
    setHoursValues(horas);
    setJustificativa('');
    setDrafts({});
    setTouched(new Set());
    setPecas({});
    setPecasVistas({});
    setATirar(undefined);
    setPasso({ kind: 'measure' });
  }, [open, inspectionId]);

  const parsed = measurable.map((item) => {
    const raw = (values[item.allocationId] ?? '').trim();
    const value = raw === '' ? null : Number(raw.replace(',', '.'));
    const valid = value === null || (Number.isFinite(value) && value >= 0);
    // Regravar o mesmo número não é medição (RN-218): não é enviado, nem exige nada.
    const changed = value !== (item.executed ?? null);
    return { item, value, valid, changed };
  });
  const allValid = parsed.every((entry) => entry.valid);

  // As horas (Fase 20): só se ajustam com a conferência aberta — encerrada, é reabrir.
  const conferenciaAberta = line?.inspection === undefined || line.inspection.status === 'PENDING';
  const horasEditaveis = canAdjust && conferenciaAberta;
  const podeTrocar = canAdjust && onReplaceFront !== undefined;
  const horasParsed = (line?.allocations ?? []).map(({ activity, allocation }) => {
    const raw = (hoursValues[allocation.id] ?? '').trim().replace(',', '.');
    const parsedHours = raw === '' ? undefined : quarterHoursFromDecimal(Number(raw));
    const quarters = parsedHours !== undefined && parsedHours.ok ? parsedHours.value : undefined;
    return {
      allocationId: allocation.id,
      expectedVersion: allocation.version,
      label: activity.label,
      valid: !horasEditaveis || quarters !== undefined,
      message: parsedHours !== undefined && !parsedHours.ok ? parsedHours.error.message : undefined,
      changed: horasEditaveis && quarters !== undefined && quarters !== allocation.quarterHours,
      quarterHours: quarters ?? allocation.quarterHours,
    };
  });
  const horasValidas = horasParsed.every((entry) => entry.valid);
  // O total é o que o botão de confirmar mostra, e ele acompanha o que está sendo digitado:
  // quem baixou de 8h para 7h confirma 7h, e lê isso antes de tocar.
  const totalQuarters = horasParsed.reduce((soma, entry) => soma + entry.quarterHours, 0);
  const podeConfirmar = canConfirm && conferenciaAberta;
  // As frentes alteradas vão juntas, numa gravação só, e o servidor confere o total
  // **final** do dia contra a jornada — o mesmo `totalQuarters` que decide o motivo aqui.
  // Gravar uma a uma conferiria os totais do meio do caminho, e o motivo seria exigido por
  // um excesso que nunca chegou a existir.
  const ajustes = horasParsed
    .filter((entry) => entry.changed)
    .map(({ allocationId, expectedVersion, quarterHours }) => ({
      allocationId,
      expectedVersion,
      quarterHours,
    }));
  // RN-291/292 — o motivo é exigido só na hora extra: o total da pessoa no dia, somando
  // todas as frentes e serviços (é o que `totalQuarters` soma, com o que está sendo
  // digitado), passou da jornada **daquele dia**. É a mesma conta do planejamento e do RDO.
  // Sem sinal, o dia também tem o que a fila ainda vai lançar para a pessoa (RN-642).
  const jornada = line?.capacityQuarterHours;
  const totalDoDia = totalQuarters + queuedQuarterHours;
  const horaExtra = isOvertime(totalDoDia, jornada);
  const justificativaExigida = ajustes.length > 0 && horaExtra;
  const justificativaOk =
    !justificativaExigida || justificativa.trim().length >= MIN_JUSTIFICATION_LENGTH;

  const frontOf = (activityId: string): DayQualityFrontView | undefined =>
    quality?.fronts.find((front) => front.activityId === activityId);

  /**
   * A alocação desta pessoa na frente × serviço — a dona das NCs dela (Fase 37, RN-474). A de
   * antes do serviço na alocação vale quando é a única da frente, como no servidor.
   */
  const alocacaoDoServico = (activityId: string, serviceId: string) => {
    const daFrente = (line?.allocations ?? []).filter(
      (candidate) => candidate.activity.activityId === activityId,
    );
    const semServico = daFrente.filter((candidate) => candidate.service === undefined);
    return (
      daFrente.find((candidate) => candidate.service?.id === serviceId) ??
      (semServico.length === 1 ? semServico[0] : undefined)
    );
  };
  const ownerOfFront =
    (activityId: string): OwnerOf =>
    (serviceId) => {
      const dona = alocacaoDoServico(activityId, serviceId);
      return dona === undefined ? undefined : `A:${dona.allocation.id}`;
    };
  /** O que esta pessoa mede no serviço — o digitado agora, ou o gravado: o teto da não conforme. */
  const measurementOfFront =
    (activityId: string) =>
    (serviceId: string): StepMeasurement | undefined => {
      const dona = alocacaoDoServico(activityId, serviceId);
      if (dona === undefined) return undefined;
      const digitado = parsed.find((entry) => entry.item.allocationId === dona.allocation.id);
      const medido =
        digitado === undefined ? dona.allocation.executedQuantity : (digitado.value ?? undefined);
      return {
        ownerKey: `A:${dona.allocation.id}`,
        measured: medido,
        unitName: dona.unit?.name,
        unitCode: dona.unit?.code,
        unitDecimals: dona.unit?.decimals,
        // As peças marcadas agora, antes de gravar: a NC pode apontá-las (Fase 37, D9).
        draftPieces: pecas[dona.allocation.id],
      };
    };

  const draftOf = (front: DayQualityFrontView): FrontDraft =>
    drafts[front.activityId] ??
    initialFrontDraft(
      front,
      quality?.nonConformities ?? [],
      undefined,
      ownerOfFront(front.activityId),
    );

  /** A frente está coberta pela RN-218 — pelo rascunho completo, ou pelo que já foi verificado hoje. */
  const frontCovered = (front: DayQualityFrontView): boolean =>
    touched.has(front.activityId)
      ? isFrontDraftComplete(front, draftOf(front))
      : frontHasVerification(front);

  const readyToSave = (() => {
    for (const entry of parsed) {
      if (!entry.changed || entry.value === null) continue;
      if (quality === undefined) return false;
      const front = frontOf(entry.item.activityId);
      if (front === undefined || front.services.length === 0) continue;
      if (!frontCovered(front)) return false;
    }
    for (const activityId of touched) {
      const front = frontOf(activityId);
      if (front !== undefined && !isFrontDraftComplete(front, draftOf(front))) return false;
    }
    return true;
  })();

  const abrirVerificacao = (front: DayQualityFrontView) => {
    setDrafts((current) =>
      current[front.activityId] === undefined
        ? {
            ...current,
            [front.activityId]: initialFrontDraft(
              front,
              quality?.nonConformities ?? [],
              undefined,
              ownerOfFront(front.activityId),
            ),
          }
        : current,
    );
    setPasso({ kind: 'verify', activityId: front.activityId });
  };

  const frenteEmVerificacao = passo.kind === 'verify' ? frontOf(passo.activityId) : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/*
        Coluna flex: o miolo rola e o rodapé fica à vista. Antes o miolo tinha 70vh e o
        diálogo rolava por fora também — no celular eram duas rolagens para achar o botão
        de confirmar.
      */}
      <DialogContent className="flex max-w-2xl flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {passo.kind === 'verify' ? (
              <ClipboardCheck className="size-5" aria-hidden="true" />
            ) : (
              <Ruler className="size-5" aria-hidden="true" />
            )}
            {passo.kind === 'verify'
              ? 'Verificação de qualidade'
              : podeConfirmar
                ? 'Conferência do dia'
                : 'Medição do dia'}
          </DialogTitle>
          <DialogDescription>
            {line?.resource.name} · {dateLabel}
            {passo.kind === 'verify'
              ? ' — o serviço executado e a checklist dele.'
              : podeConfirmar
                ? ' — as horas, o que cada frente rendeu, a qualidade e as fotos. Confirmar encerra o dia.'
                : ' — o que cada frente rendeu de fato, e as fotos que comprovam.'}
          </DialogDescription>
        </DialogHeader>

        {passo.kind === 'verify' && frenteEmVerificacao !== undefined ? (
          <div className="relative flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto">
            <QualityVerificationStep
              front={frenteEmVerificacao}
              draft={draftOf(frenteEmVerificacao)}
              nonConformities={quality?.nonConformities ?? []}
              disabled={isSaving}
              photos={nonConformityPhotos}
              nonConformityOwners={quality?.nonConformityOwners}
              measurementOf={measurementOfFront(frenteEmVerificacao.activityId)}
              pendingNonConformities={quality?.pendingNonConformities}
              onChange={(next) => {
                setDrafts((current) => ({ ...current, [frenteEmVerificacao.activityId]: next }));
                setTouched((current) => new Set([...current, frenteEmVerificacao.activityId]));
              }}
              onBack={() => setPasso({ kind: 'measure' })}
            />
          </div>
        ) : (
          <div className="relative flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto">
            <section className="flex flex-col gap-3">
              <h3 className="text-sm font-medium">Execução real</h3>

              {quantityOfTeam !== undefined && (
                <p className="text-sm text-(--color-content-muted)">
                  A quantidade deste dia é da {quantityOfTeam}: lance-a uma vez em Medição por
                  equipe. Aqui ficam as horas, a confirmação e as fotos da pessoa.
                </p>
              )}
              {measurable.length === 0 && !horasEditaveis && quantityOfTeam === undefined && (
                <p className="text-sm text-(--color-content-muted)">
                  Nenhuma frente deste dia tem unidade de medição. Defina a unidade da disciplina em
                  Estrutura → Medição para passar a medir.
                </p>
              )}
              {canAdjust && !conferenciaAberta && (
                <p className="text-sm text-(--color-content-muted)">
                  As horas só se ajustam, e a frente só se tira, com a conferência aberta — reabra
                  pelo cartão para mudá-las.
                  {podeTrocar && ' A atividade lançada errada se troca pelo “Trocar”.'}
                </p>
              )}

              {(line?.allocations ?? []).map(({ activity, allocation, service }) => {
                const entry = parsed.find(
                  (candidate) => candidate.item.allocationId === allocation.id,
                );
                const horas = horasParsed.find(
                  (candidate) => candidate.allocationId === allocation.id,
                );
                const front = frontOf(activity.activityId);
                if (
                  entry === undefined &&
                  !horasEditaveis &&
                  !podeTrocar &&
                  quantityOfTeam === undefined
                ) {
                  return null;
                }
                return (
                  <div key={allocation.id} className="flex flex-col gap-2">
                    {/* No celular as horas descem para baixo da quantidade: lado a lado, o
                        rótulo da frente em três linhas desalinhava os dois campos. */}
                    <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-[1fr_auto]">
                      {entry === undefined ? (
                        <p className="text-sm">
                          {activity.label}
                          {service !== undefined && ` · ${service.name}`}
                          <span className="block text-xs text-(--color-content-muted)">
                            {service === undefined
                              ? 'sem serviço — lançada antes do cadastro, e por isso não mede'
                              : quantityOfTeam !== undefined
                                ? `a quantidade é da ${quantityOfTeam} — meça em Medição por equipe`
                                : 'o serviço não tem unidade — cadastre em Configurações → Serviços'}
                          </span>
                        </p>
                      ) : (
                        <Field
                          label={`${entry.item.label} (${entry.item.unit.name})`}
                          htmlFor={`medicao-${allocation.id}`}
                          hint={
                            entry.item.planned === undefined
                              ? `${Hours.ofQuarters(allocation.quarterHours).format()} no dia · sem meta lançada`
                              : `${Hours.ofQuarters(allocation.quarterHours).format()} no dia · meta ${formatQuantity(entry.item.planned, entry.item.unit)}`
                          }
                        >
                          <div className="flex gap-2">
                            <Input
                              id={`medicao-${allocation.id}`}
                              type="number"
                              inputMode="decimal"
                              step={10 ** -entry.item.unit.decimals}
                              min={0}
                              value={values[allocation.id] ?? ''}
                              onChange={(event) => {
                                setValues((current) => ({
                                  ...current,
                                  [allocation.id]: event.target.value,
                                }));
                              }}
                            />
                            {/* O m² — alvenaria, reboco, gesso… — mede pelo pano (RN-379). */}
                            {isAreaUnitCode(entry.item.unit.code) && (
                              <AreaMeasurementButton
                                subject={entry.item.label}
                                unitName={entry.item.unit.name}
                                decimals={entry.item.unit.decimals}
                                disabled={isSaving}
                                onApply={(total) => {
                                  setValues((current) => ({
                                    ...current,
                                    [allocation.id]: String(total),
                                  }));
                                }}
                              />
                            )}
                          </div>
                        </Field>
                      )}
                      {(horasEditaveis || podeTrocar) && (
                        <div className="flex items-end gap-2">
                          {horasEditaveis && (
                            <Field
                              label="Horas"
                              htmlFor={`horas-${allocation.id}`}
                              {...(horas?.message === undefined ? {} : { error: horas.message })}
                            >
                              <Input
                                id={`horas-${allocation.id}`}
                                className="w-24"
                                type="number"
                                inputMode="decimal"
                                step={0.25}
                                min={0.25}
                                aria-label={`Horas de ${activity.label}`}
                                value={hoursValues[allocation.id] ?? ''}
                                onChange={(event) => {
                                  setHoursValues((current) => ({
                                    ...current,
                                    [allocation.id]: event.target.value,
                                  }));
                                }}
                              />
                            </Field>
                          )}
                          {podeTrocar && (
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={`Trocar ${rotuloDaFrente(activity.label, service?.name)} por outra atividade`}
                              disabled={isSaving}
                              onClick={() => onReplaceFront?.(allocation.id)}
                            >
                              <ArrowLeftRight aria-hidden="true" />
                              Trocar
                            </Button>
                          )}
                          {horasEditaveis && onRemoveFront !== undefined && (
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={`Tirar ${rotuloDaFrente(activity.label, service?.name)} do dia`}
                              disabled={isSaving}
                              onClick={() => setATirar(allocation.id)}
                            >
                              <Trash2 aria-hidden="true" />
                              Tirar
                            </Button>
                          )}
                        </div>
                      )}
                    </div>

                    {aTirar === allocation.id && onRemoveFront !== undefined && horasEditaveis && (
                      <div
                        role="group"
                        aria-label={`Confirmar: tirar ${rotuloDaFrente(activity.label, service?.name)}`}
                        className="flex flex-col gap-2 rounded-md border border-(--color-state-danger) p-3"
                      >
                        <p className="text-sm">
                          Tirar <strong>{rotuloDaFrente(activity.label, service?.name)}</strong> do
                          dia de {line?.resource.name}? Saem as horas e a quantidade desta frente, e
                          a não conformidade dela é cancelada. Se ninguém mais estiver nela hoje,
                          saem também a verificação de qualidade e as peças e cômodos marcados hoje.
                          Fica no histórico do dia.
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            variant="danger"
                            className="min-h-11 md:h-9"
                            disabled={isSaving}
                            onClick={() => {
                              setATirar(undefined);
                              onRemoveFront({
                                allocationId: allocation.id,
                                expectedVersion: allocation.version,
                              });
                            }}
                          >
                            Tirar a frente
                          </Button>
                          <Button
                            variant="secondary"
                            className="min-h-11 md:h-9"
                            onClick={() => setATirar(undefined)}
                          >
                            Manter
                          </Button>
                        </div>
                      </div>
                    )}

                    {entry !== undefined && pieces !== undefined && service !== undefined && (
                      <PecasDoDia
                        activityId={activity.activityId}
                        serviceId={service.id}
                        date={pieces.date}
                        load={pieces.load}
                        unit={entry.item.unit}
                        selected={pecas[allocation.id]}
                        disabled={isSaving}
                        onChange={({ pieceNames, total, previous }) => {
                          setPecas((current) => ({ ...current, [allocation.id]: pieceNames }));
                          setPecasVistas((current) => ({ ...current, [allocation.id]: previous }));
                          // A soma preenche o campo, que continua editável (RN-382). Sem
                          // peça marcada, o número que as peças puseram vai a zero; o
                          // digitado à mão fica como a pessoa o deixou.
                          const preenchido = total === undefined ? '0' : String(total);
                          const anterior = doPreenchimento[allocation.id];
                          if (total !== undefined || values[allocation.id] === anterior) {
                            setValues((current) => ({
                              ...current,
                              [allocation.id]: preenchido,
                            }));
                            setDoPreenchimento((current) => ({
                              ...current,
                              [allocation.id]: preenchido,
                            }));
                          }
                        }}
                      />
                    )}

                    {entry !== undefined && (
                      <QualityStatus
                        front={front}
                        loading={quality === undefined}
                        draft={front === undefined ? undefined : draftOf(front)}
                        touched={touched.has(activity.activityId)}
                        needsVerification={entry.changed && entry.value !== null}
                        onVerify={front === undefined ? undefined : () => abrirVerificacao(front)}
                      />
                    )}
                  </div>
                );
              })}

              {ajustes.length > 0 && (
                <Field
                  label="Justificativa do ajuste"
                  htmlFor="medicao-justificativa"
                  required={justificativaExigida}
                  hint={
                    justificativaExigida
                      ? `Hora extra: ${Hours.ofQuarters(totalDoDia).format()} no dia${queuedQuarterHours > 0 ? ` (${Hours.ofQuarters(queuedQuarterHours).format()} de lançamento aguardando envio)` : ''}, e a jornada é ${Hours.ofQuarters(jornada ?? 0).format()}. Escreva o motivo — ao menos ${String(MIN_JUSTIFICATION_LENGTH)} caracteres.`
                      : 'Só é exigida se o total do dia passar da jornada (RN-292); fica no histórico se preenchida.'
                  }
                >
                  <Textarea
                    id="medicao-justificativa"
                    rows={2}
                    value={justificativa}
                    onChange={(event) => setJustificativa(event.target.value)}
                  />
                </Field>
              )}
            </section>

            <AttachmentGallery
              attachments={attachments}
              usage={usage}
              {...(caption === undefined ? {} : { caption: { ...caption, required: false } })}
              canAttach={canAttach}
              canRemove={canRemove}
              isPending={isAttaching}
              {...(attachErrorMessage === undefined ? {} : { errorMessage: attachErrorMessage })}
              resolveUrl={resolveUrl}
              onAttach={onAttach}
              onRemove={onRemoveAttachment}
            />
          </div>
        )}

        {errorMessage !== undefined && (
          <p role="alert" className="text-sm text-(--color-state-danger)">
            {errorMessage}
          </p>
        )}

        <DialogFooter>
          {passo.kind === 'verify' ? (
            <Button
              variant="primary"
              className="min-h-11"
              onClick={() => setPasso({ kind: 'measure' })}
            >
              Pronto — voltar às quantidades
            </Button>
          ) : (
            <>
              {onHistory !== undefined && (line?.changeCount ?? 0) > 0 && (
                <Button variant="ghost" className="mr-auto" onClick={onHistory}>
                  <History aria-hidden="true" />
                  Histórico
                </Button>
              )}
              <Button
                variant="secondary"
                onClick={() => {
                  onOpenChange(false);
                }}
              >
                Fechar
              </Button>
              {(measurable.length > 0 || horasEditaveis || podeConfirmar) && (
                <div className="flex min-w-0 flex-col items-stretch gap-1 sm:items-end sm:text-right">
                  <Button
                    variant="primary"
                    className="min-h-11"
                    disabled={
                      isSaving || !allValid || !readyToSave || !horasValidas || !justificativaOk
                    }
                    onClick={() => {
                      const medicoes = parsed
                        .filter((entry) => entry.changed)
                        .map((entry) => ({
                          allocationId: entry.item.allocationId,
                          executedQuantity: entry.value,
                        }));
                      const verificacoes = [...touched].flatMap((activityId) => {
                        const front = frontOf(activityId);
                        return front === undefined ? [] : frontDraftToInput(front, draftOf(front));
                      });
                      const fotos = [...touched].flatMap((activityId) => {
                        const front = frontOf(activityId);
                        return front === undefined ? [] : pendingPhotos(front, draftOf(front));
                      });
                      const pecasGravar = measurable.flatMap((item) => {
                        const pieceNames = pecas[item.allocationId];
                        const serviceId = (line?.allocations ?? []).find(
                          (candidate) => candidate.allocation.id === item.allocationId,
                        )?.service?.id;
                        return pieceNames === undefined || serviceId === undefined
                          ? []
                          : [
                              {
                                allocationId: item.allocationId,
                                activityId: item.activityId,
                                serviceId,
                                pieceNames,
                                previousPieceNames: pecasVistas[item.allocationId] ?? [],
                              },
                            ];
                      });
                      // Sem ajuste de horas, a chamada é a de sempre — com dois argumentos.
                      if (
                        ajustes.length === 0 &&
                        !podeConfirmar &&
                        pecasGravar.length === 0 &&
                        fotos.length === 0
                      ) {
                        onSave(medicoes, verificacoes);
                        return;
                      }
                      onSave(medicoes, verificacoes, {
                        adjustments: ajustes,
                        ...(justificativa.trim() === ''
                          ? {}
                          : { justification: justificativa.trim() }),
                        ...(podeConfirmar ? { confirm: true } : {}),
                        ...(pecasGravar.length === 0 ? {} : { pieces: pecasGravar }),
                        ...(fotos.length === 0 ? {} : { photos: fotos }),
                      });
                    }}
                  >
                    {isSaving
                      ? 'Salvando…'
                      : podeConfirmar
                        ? `Confirmar ${Hours.ofQuarters(totalQuarters).format()}`
                        : ajustes.length > 0
                          ? 'Salvar horas e medição'
                          : 'Salvar medição'}
                  </Button>
                  {!readyToSave ? (
                    <p className="text-xs text-(--color-content-muted)">
                      {quality === undefined
                        ? 'Carregando a verificação de qualidade…'
                        : 'Responda a checklist da frente medida antes de gravar.'}
                    </p>
                  ) : (
                    podeConfirmar && (
                      <p className="text-xs text-(--color-content-muted)">
                        {ajustes.length > 0
                          ? 'O dia encerra como ajustado — depois disso, as horas só mudam reabrindo.'
                          : 'Encerra o dia desta pessoa — depois disso, as horas só mudam reabrindo.'}
                      </p>
                    )
                  )}
                </div>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * O estado da verificação de uma frente, sob o campo de quantidade.
 *
 * Diz três coisas em uma linha: se há serviço para declarar, se a frente já está coberta
 * — pelo rascunho ou por quem verificou antes —, e se a quantidade nova está esperando por
 * isso. O botão entra no segundo passo.
 */
function QualityStatus({
  front,
  loading,
  draft,
  touched,
  needsVerification,
  onVerify,
}: {
  front: DayQualityFrontView | undefined;
  loading: boolean;
  draft: FrontDraft | undefined;
  touched: boolean;
  needsVerification: boolean;
  onVerify: (() => void) | undefined;
}) {
  if (loading) {
    return (
      <p className="text-xs text-(--color-content-muted)">Carregando a verificação de qualidade…</p>
    );
  }
  if (front === undefined || front.services.length === 0) {
    return (
      <p className="text-xs text-(--color-content-muted)">
        Sem serviço cadastrado para esta disciplina — mede como sempre.
      </p>
    );
  }

  const covered =
    touched && draft !== undefined
      ? isFrontDraftComplete(front, draft)
      : frontHasVerification(front);
  const resumo =
    touched && draft !== undefined
      ? summarizeFrontDraft(front, draft)
      : front.services.reduce(
          (acc, service) => ({
            approved: acc.approved + (service.verification?.summary.approved ?? 0),
            rejected: acc.rejected + (service.verification?.summary.rejected ?? 0),
            total: acc.total + (service.verification?.summary.total ?? 0),
          }),
          { approved: 0, rejected: 0, total: 0 },
        );

  return (
    <div className="flex flex-wrap items-center gap-2">
      {covered ? (
        <Badge tone={resumo.rejected > 0 ? 'warn' : 'ok'}>
          {resumo.total === 0
            ? 'Serviço declarado'
            : `Qualidade ${String(resumo.approved)}/${String(resumo.total)}${resumo.rejected > 0 ? ` · ${String(resumo.rejected)} NC` : ''}`}
          {touched && ' (a gravar)'}
        </Badge>
      ) : needsVerification ? (
        <Badge tone="warn">Verificação pendente — obrigatória para gravar quantidade nova</Badge>
      ) : (
        <Badge tone="neutral">Sem verificação</Badge>
      )}
      <Button
        variant="secondary"
        size="sm"
        className="min-h-11 md:min-h-9"
        onClick={onVerify}
        aria-label={`Verificar qualidade de ${front.label}`}
      >
        <ClipboardCheck aria-hidden="true" />
        {covered ? 'Ver verificação' : 'Verificar qualidade'}
      </Button>
    </div>
  );
}

/** "Alvenaria — 3º Pavimento · VERGAS / CONTRAVERGAS": a frente e o serviço, como a linha os mostra. */
function rotuloDaFrente(frente: string, servico: string | undefined): string {
  return servico === undefined ? frente : `${frente} · ${servico}`;
}
